import { useRouteLoaderData } from "react-router"
import { type loader as rootLoader } from "#app/root.tsx"
import { type Role } from "./permissions.server"

type User = {
	name: string | null
	id: string
	username: string | null
	firstName: string | null
	lastName: string | null
	image: { id: string } | null
	roles: Array<{
		name: Role
		permissions: Array<{
			action: string
			entity: string
			access: string
		}>
	}>
}

function isUser(user: unknown): user is User {
	return user != null && typeof user === "object" && "id" in (user as any)
}

export function useOptionalUser() {
	const data = useRouteLoaderData<typeof rootLoader>("root")
	if (!data?.user || !isUser(data.user)) {
		return undefined
	}
	return data.user as User
}

export function useUser() {
	const maybeUser = useOptionalUser()
	if (!maybeUser) {
		throw new Error(
			"No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.",
		)
	}
	return maybeUser
}

type Action = "create" | "read" | "update" | "delete"
type Entity = "user" | "order"
type Access = "own" | "any" | "own,any" | "any,own"
export type PermissionString =
	| `${Action}:${Entity}`
	| `${Action}:${Entity}:${Access}`
export function parsePermissionString(permissionString: PermissionString) {
	const [action, entity, access] = permissionString.split(":") as [
		Action,
		Entity,
		Access | undefined,
	]
	return {
		action,
		entity,
		access: access ? (access.split(",") as Array<Access>) : undefined,
	}
}

export function userHasPermission(
	user: Pick<User, "roles"> | null | undefined,
	permission: PermissionString,
) {
	if (!user) return false
	const { action, entity, access } = parsePermissionString(permission)
	return user.roles.some(role =>
		role.permissions.some(
			perm =>
				perm.entity === entity &&
				perm.action === action &&
				(!access || access.includes(perm.access)),
		),
	)
}

export function userHasRole(user: Pick<User, "roles"> | null, role: Role) {
	if (!user) return false
	return user.roles.some(r => r.name === role)
}
